import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { GroupTrialsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { convertToApproximateDateString } from "src/helpers/date";
import { findLookupValue } from "src/helpers/lookupHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialGroupActions from "../../../actions/trialGroup";
import {
    RestrictedLayout,
    TruncatedCell,
} from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./GroupTrialsPage.scss";

interface IGroupTrialsPageParams {
    groupId: number;
}

interface IGroupTrialsPageProps extends IRouterProps<IGroupTrialsPageParams> {
    groupId: number;

    // From state
    formFilter: IFormFilter;
    group: Dtos.Group;
    trialGroups: Dtos.TrialGroup[] | undefined;
    lookups: Dtos.Lookup[];

    loadingGroup: boolean;
    loadGroupSuccess: boolean;
    loadGroupFailure: boolean;
    loadGroupErrorCode: string;

    loadingTrialGroups: boolean;
    loadTrialGroupsSuccess: boolean;
    loadTrialGroupsFailure: boolean;
    loadTrialGroupsErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];

    pageSize: number;
}

interface IGroupTrialsPageActions extends IRouterActions {
    clearTrialGroups: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroups: TrialGroupActions.ITrialGroupsLoadByGroupIdActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

    clearGroup: GroupActions.IGroupClearActionCreator;
    loadGroup: GroupActions.IGroupLoadByIdActionCreator;

}

type GroupTrialsPageProps = IGroupTrialsPageProps & IGroupTrialsPageActions;

class GroupPage extends React.PureComponent<GroupTrialsPageProps, any> {
    initialised: boolean;

    constructor(props: GroupTrialsPageProps) {
        super(props);
        this.initialised = false;
        this.clearGroup = this.clearGroup.bind(this);
        this.toggleExportMenu = this.toggleExportMenu.bind(this);

        this.state = {
            exportToggle: false,
        };
    }

    componentDidMount() {
        const {
            loadTrialGroups,
            groupId,
            permissions,
            setDrawerItems,
            setBreadcrumbs,
            loadGroup
        } = this.props;
        

        if (groupId) {
            setDrawerItems(GroupSubMenu(groupId, permissions));
            setBreadcrumbs(GroupTrialsBreadcrumbs(groupId));
            loadGroup(groupId);
            loadTrialGroups(groupId);
        }
    }

    componentDidUpdate(prevProps: GroupTrialsPageProps, _prevState) {
        const {
            group,
            groupId,
            loadGroup,
            setDrawerItems,
            setBreadcrumbs,
            permissions,
            loadTrialGroupsFailure,
            loadGroupFailure,
            navigate,
            loadGroupErrorCode,
            loadTrialGroupsErrorCode,
        } = this.props;

        if (loadGroupFailure) {
            navigate(`/error/${loadGroupErrorCode}`);
        }
        if (loadTrialGroupsFailure) {
            navigate(`/error/${loadTrialGroupsErrorCode}`);
        }

        if (groupId && group?.name) {
            setDrawerItems(GroupSubMenu(groupId, permissions));
            setBreadcrumbs(GroupTrialsBreadcrumbs(groupId, group.name));
        }

        if (groupId && prevProps.groupId != groupId) {
            loadGroup(groupId);
        }
    }

    componentWillUnmount() {
        this.clearGroup();
    }

    clearGroup() {
        this.props.clearGroup();
        this.props.clearTrialGroups();
    }

    toggleExportMenu() {
        this.setState({
            exportToggle: !this.state.exportToggle,
        });
    }

    showTrials(): boolean {
        const { loadingTrialGroups, loadTrialGroupsSuccess } = this.props;

        if (!loadingTrialGroups && loadTrialGroupsSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const {
            groupId,
            group,
            loadingGroup,
            loadingTrialGroups,
            permissions,
        } = this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.TrialGroupView}
                subMenuTitle={group ? group.name : ""}
                subMenuItems={GroupSubMenu(groupId, permissions)}
                loading={loadingGroup || loadingTrialGroups}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trials</h1>
                    </div>
                </div>
                {!this.showTrials() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showTrials() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const {
            groupId,
            trialGroups,
            lookups,
            pageSize,
            setPageSize,
            permissions,
        } = this.props;

        if (!trialGroups || trialGroups.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no trials to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = trialGroups.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialGroups}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : trialGroups.length
                    }
                    pageSize={showPagination ? pageSize : trialGroups.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trials to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const trialId = rowInfo.original.id;
                                const canView =
                                    AuthenticationHelper.hasPermission(
                                        permissions,
                                        Dtos.Permission.TrialGroupView,
                                        trialId,
                                        undefined
                                    );
                                const canViewPersonnelRoles =
                                    AuthenticationHelper.hasPermission(
                                        permissions,
                                        Dtos.Permission.TrialPersonnelRoleView,
                                        trialId,
                                        undefined
                                    );
                                if (
                                    !canViewPersonnelRoles &&
                                    column.id == "personnelCount"
                                ) {
                                    return;
                                }
                                if (!canView && column.id != "personnelCount") {
                                    return;
                                }
                                const url: string =
                                    column.id == "personnelCount"
                                        ? "/trials/" +
                                          rowInfo.original.id +
                                          "/personnel"
                                        : `/groups/${groupId}/trials/${rowInfo.original.trial.id}/trialgroup/${rowInfo.original.id}/details`;
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "trial.studyAcronym",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.TRIAL}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "trial.studyAcronym",
                            Header: "Name",
                            accessor: "trial.studyAcronym",
                            maxWidth: 200,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "trial.protocolID",
                            Header: "Protocol Id",
                            accessor: "trial.protocolID",
                            maxWidth: 500,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "trial.shortTitle",
                            Header: "Short Title",
                            accessor: "trial.shortTitle",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "trial.trialStatusId",
                            Header: "Status",
                            accessor: "trial.trialStatusId",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {findLookupValue(
                                            "Trial",
                                            "TrialStatusId",
                                            props.value,
                                            lookups
                                        )}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "trial.personnelCount",
                            Header: "Roles",
                            accessor: "trial.personnelCount",
                            maxWidth: 80,
                            Cell: (props) => (
                                <div>
                                    <span className="text-info">
                                        <u>{props.value || "0"}</u>
                                    </span>
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: GroupTrialsPageProps
): IGroupTrialsPageProps => {
    let group: Dtos.Group | undefined = !(
        state.groups.formData instanceof Array
    )
        ? state.groups.formData
        : undefined;
    let trialGroups: Dtos.TrialGroup[] | undefined =
        state.trialGroup.formData instanceof Array
            ? state.trialGroup.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        history: ownProps.history,
        location: ownProps.location,
        match: ownProps.match,

        trialGroups: trialGroups,
        formFilter: state.trials.formFilter,
        lookups: state.trialGroup.lookups,

        loadingTrialGroups:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupsSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupsFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupsErrorCode: state.trialGroup.loadState.errorCode,

        groupId:
            ownProps.match &&
            ownProps.match.params.groupId.toString() != "create"
                ? ownProps.match.params.groupId
                : undefined!,
        group: group!,

        loadingGroup:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupErrorCode: state.groups.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,

        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: GroupTrialsPageProps
): IGroupTrialsPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadTrialGroups: bindActionCreators(
            TrialGroupActions.LoadTrialGroupsByGroupId,
            dispatch
        ),
        clearTrialGroups: bindActionCreators(TrialGroupActions.Clear, dispatch),

        loadGroup: bindActionCreators(GroupActions.LoadGroupById, dispatch),
        clearGroup: bindActionCreators(GroupActions.Clear, dispatch),

        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<IGroupTrialsPageParams, GroupTrialsPageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(GroupPage) as unknown as ComponentType<GroupTrialsPageProps>
) as ComponentType<any>;
